import { type ReactNode } from 'react';
import { Link } from 'react-router';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import {
  ArrowUpRight,
  Banknote,
  BriefcaseBusiness,
  Building2,
  CircleAlert,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Badge, Button, MetricCard, RiskBadge } from '@helios/ui';
import { BreakdownBar, type DrillCohort, type DrillRecord } from '@helios/ui/charts';
import { useAuth } from '@/features/auth/authContext';
import type {
  CommandCenterPayload,
  CommandCenterProjectMarker,
  ExecutiveReadModel,
} from '@/services/commandCenterClient';
import { FinanceCompanyProfitBridge } from '@/features/finance/FinanceCompanyProfitBridge';
import { FinanceCashOutlookPanel } from '@/features/finance/FinanceCashOutlookPanel';
import { formatRecordedAmount } from '@/features/finance/financeInvoices.types';
import { companyMetricScope } from '@/features/finance/financeOverview.view';
import { EngineeringBibleStatusCard } from '@/components/system/EngineeringBibleStatusCard';
import { SystemStatusArea } from '@/components/system/SystemStatusArea';
import { RealtimeEventFeed } from '@/features/realtime/RealtimeEventFeed';
import type { useRealtimeDashboard } from '@/hooks/useRealtimeDashboard';
import { ExecutiveOverview, type ExecutiveOverviewNavigation } from './ExecutiveOverview';

export type ExecutiveView =
  | 'overview'
  | 'finance'
  | 'delivery'
  | 'growth'
  | 'risk'
  | 'intelligence'
  | 'system';
type Inspect = (cohort: DrillCohort) => void;
type Props = {
  payload: CommandCenterPayload;
  view: ExecutiveView;
  inspect: Inspect;
  priorities?: ReactNode;
  overviewNavigation?: ExecutiveOverviewNavigation;
  onOverviewNavigationChange?: (navigation: ExecutiveOverviewNavigation) => void;
  realtime: ReturnType<typeof useRealtimeDashboard>;
};
const forecastStates = [
  'current',
  'unreviewed',
  'currency_unknown',
  'missing',
  'stale',
  'inconsistent',
  'contract_changed',
  'restricted',
] as const;
const riskOrder = ['CRITICAL', 'HIGH', 'MODERATE', 'LOW'] as const;
const number = (value: number | null | undefined) =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'Unavailable'
    : new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
const date = (value: string | null | undefined) =>
  value && Number.isFinite(new Date(value).getTime())
    ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' })
    : 'Date unavailable';
const label = (value: string) =>
  value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/^./, (part) => part.toUpperCase());
const recorded = (value: string | null | undefined, currency?: string | null) =>
  `${formatRecordedAmount(value)}${currency ? ` ${currency}` : ''}`;
const healthScore = (row: CommandCenterProjectMarker): number | null =>
  row.healthSource?.availability === 'recorded_model' &&
  row.healthSource.score === row.healthScore &&
  row.healthSource.snapshotId &&
  row.healthSource.calculatedAt
    ? row.healthScore
    : null;
const healthLabel = (row: CommandCenterProjectMarker) => {
  const score = healthScore(row);
  return score === null
    ? 'Health unavailable · no valid recorded model'
    : `${score}/100 recorded model health`;
};
const compareHealth = (a: CommandCenterProjectMarker, b: CommandCenterProjectMarker) => {
  const left = healthScore(a),
    right = healthScore(b);
  return left === null ? (right === null ? 0 : 1) : right === null ? -1 : left - right;
};
const isProjectException = (row: CommandCenterProjectMarker) => {
  const score = healthScore(row);
  return ['HIGH', 'CRITICAL'].includes(row.riskLevel) || (score !== null && score < 75);
};
const projectRecord = (row: CommandCenterProjectMarker): DrillRecord => ({
  id: row.id,
  title: `${row.projectNumber} · ${row.name}`,
  subtitle: `${healthLabel(row)} · ${label(row.riskLevel)} risk`,
  date: row.healthSource?.calculatedAt ?? row.updatedAt,
  href: `/projects/overview?projectId=${encodeURIComponent(row.id)}`,
});

export function ExecutiveDashboard({
  payload,
  view,
  inspect,
  priorities,
  realtime,
  overviewNavigation,
  onOverviewNavigationChange,
}: Props) {
  const { permissions } = useAuth();
  const read = payload.executive;
  const finance = read?.finance.data;
  const owner = read?.owner.data;
  const ops = read?.operations.data;
  const growth = read?.growth?.data;
  const projects = payload.projectMap.markers;
  const recordedHealthCount = projects.filter((row) => healthScore(row) !== null).length;
  const unavailableHealthCount = projects.length - recordedHealthCount;
  const healthCoverage = `${recordedHealthCount}/${projects.length} selected projects have a valid recorded model · ${unavailableHealthCount} unavailable`;
  const coverage = payload.projectPerformance.healthCoverage;
  const healthSourceAvailable =
    coverage?.recordedCount === recordedHealthCount &&
    coverage.unavailableCount === unavailableHealthCount &&
    coverage.totalProjects === projects.length;
  const current = finance?.profitability.filter((row) => row.forecastCoverage === 'current').length;
  const forecasts = finance?.profitability.length;
  const company = finance?.accounting;
  const companyScope = companyMetricScope(company, 'revenue');
  const cashCurrency = finance?.cashSource?.currency;
  const canReadAccounting = hasPermission(permissions, PERMISSIONS.ACCOUNTING_CONNECTIONS_READ);
  const canReadProjects =
    payload.coverage?.canReadProjects ?? hasPermission(permissions, PERMISSIONS.PROJECTS_READ);
  const decisions = owner?.decisions;
  const fullActiveAlerts = payload.coverage?.activeAlertCount;
  const activeProjects = canReadProjects ? payload.summary.activeProjects : null;
  const openProject = (row: CommandCenterProjectMarker) =>
    inspect({
      id: row.id,
      label: row.name,
      measure: 'Recorded project evidence',
      basis:
        healthScore(row) === null
          ? 'No valid recorded model history. The default project score is not evidence.'
          : `Recorded model ${row.healthSource?.snapshotId} · calculated ${date(row.healthSource?.calculatedAt)}. Model inputs are recorded risk, status, phase and finance; this is not verified schedule or safety performance.`,
      records: [projectRecord(row)],
      value: healthScore(row) === null ? 'Unavailable' : `${healthScore(row)}/100`,
      viewAllHref: `/projects/overview?projectId=${encodeURIComponent(row.id)}`,
      viewAllLabel: 'Open project controls',
    });
  const metrics = (
    <section
      aria-label="Executive source measures"
      className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6"
    >
      <MetricCard
        label="Company revenue"
        value={
          canReadAccounting && company?.revenue !== null && company?.revenue !== undefined
            ? recorded(company.revenue, companyScope.currency)
            : null
        }
        hint={
          company
            ? `${companyScope.dateLabel} · ${companyScope.basis || 'basis unreported'}`
            : 'Imported company statement required'
        }
        icon={<Banknote />}
        onClick={() => inspectCompany(read, inspect)}
      />
      <MetricCard
        label="Recorded cash"
        value={
          finance?.summary.netCashPosition === null ||
          finance?.summary.netCashPosition === undefined
            ? null
            : recorded(finance.summary.netCashPosition, cashCurrency)
        }
        hint={
          cashCurrency
            ? `${cashCurrency} · ${finance?.cashSource?.source === 'confirmed_accounts' ? 'confirmed account group' : 'imported balance sheet'}`
            : 'Common currency and confirmed source required'
        }
        icon={<Banknote />}
        onClick={() => inspectCash(read, inspect)}
      />
      <MetricCard
        label="Forecast coverage"
        value={current === undefined ? null : `${current}/${forecasts}`}
        hint="Current human-approved WIP · declared currency cohort"
        icon={<ShieldCheck />}
        onClick={() => inspectForecast(read, inspect)}
      />
      <MetricCard
        label="Active / on hold"
        value={activeProjects}
        hint={`${projects.length} portfolio records · selected filters`}
        icon={<Building2 />}
        onClick={() =>
          inspect({
            id: 'active-projects',
            label: 'Active and on-hold projects',
            measure: 'Project records',
            value: number(activeProjects),
            basis: payload.coverage?.projectCohort ?? 'Selected recorded portfolio',
            records: projects
              .filter((row) => ['ACTIVE', 'ON_HOLD'].includes(row.status))
              .map(projectRecord),
            recordsAvailable: canReadProjects,
            recordsUnavailableReason: 'Project source access is required.',
            viewAllHref: canReadProjects ? '/projects' : undefined,
          })
        }
      />
      <MetricCard
        label="Source decisions"
        value={decisions?.total ?? null}
        hint={
          decisions
            ? `${decisions.items.length} preview rows · domain grants retained`
            : 'Permitted native sources required'
        }
        icon={<BriefcaseBusiness />}
        onClick={() => inspectDecisions(read, inspect)}
      />
      <MetricCard
        label="Reported on site"
        value={ops?.fieldToday?.totalHeadcount ?? null}
        hint={
          ops?.fieldToday
            ? `${ops.fieldToday.date} · ${ops.fieldToday.timeZone} · ${ops.fieldToday.totalCrews} reported crews`
            : 'Current field report source required'
        }
        icon={<Users />}
        onClick={() => inspectField(read, inspect)}
      />
    </section>
  );
  const health = (
    <BreakdownBar
      title="Portfolio health distribution"
      basis={
        !canReadProjects
          ? 'Project model history is restricted.'
          : !healthSourceAvailable
            ? 'Recorded model coverage is unavailable. Refresh the current source.'
            : `${healthCoverage}. Bands use latest recorded model history, not schedule or safety performance.`
      }
      data={(canReadProjects && healthSourceAvailable
        ? payload.projectPerformance.healthDistribution
        : []
      ).map((row) => ({
        id: row.band,
        label: row.band === 'UNAVAILABLE' ? 'No recorded model' : row.band,
        values: { projects: row.count },
      }))}
      series={[{ key: 'projects', label: 'Project records', token: 'context-primary' }]}
      format="count"
      height={180}
      state={
        !canReadProjects || !healthSourceAvailable
          ? 'unavailable'
          : projects.length
            ? 'ok'
            : 'empty'
      }
      unavailableMessage={
        !canReadProjects
          ? 'Project records are restricted.'
          : 'Recorded model coverage is unavailable. Refresh the current source.'
      }
      emptyMessage="No projects match the selected portfolio filters."
      onDrill={(id) => {
        if (!canReadProjects) return;
        const [low, high] = id === '<60' ? [0, 59] : id.split('-').map(Number);
        const rows = projects.filter((row) => {
          const score = healthScore(row);
          return id === 'UNAVAILABLE'
            ? score === null
            : score !== null && score >= low! && score <= high!;
        });
        inspect({
          id,
          label: id === 'UNAVAILABLE' ? 'No recorded model' : `Recorded health ${id}`,
          measure: 'Project records',
          value: String(rows.length),
          basis: `${rows.length} of ${projects.length} selected projects. ${healthCoverage}. Default scores are excluded from model bands; model health is not a measured safety or schedule score.`,
          records: rows.map(projectRecord),
          viewAllHref: '/projects',
        });
      }}
    />
  );
  const forecastsPanel = (
    <BreakdownBar
      title="Forecast evidence coverage"
      basis={`${forecasts ?? '—'} eligible active/planning financial projects · company-wide, unaffected by portfolio filters.`}
      description="Current means a qualifying human-reviewed preparation with a declared currency. Legacy and unknown-currency records stay outside reviewed forecasts."
      data={forecastStates.map((state) => ({
        id: state,
        label: label(state),
        values: {
          projects:
            finance?.profitability.filter((row) => (row.forecastCoverage ?? 'missing') === state)
              .length ?? null,
        },
      }))}
      series={[{ key: 'projects', label: 'Project records', token: 'context-primary' }]}
      format="count"
      height={180}
      state={!finance ? 'unavailable' : forecasts ? 'ok' : 'empty'}
      unavailableMessage={read?.finance.note ?? 'Finance source is unavailable.'}
      emptyMessage="No eligible financial projects are recorded."
      onDrill={(id) => inspectForecast(read, inspect, id)}
    />
  );
  const companyPanel = (
    <Panel
      title="Company financial position"
      subtitle={
        company
          ? `${companyScope.dateLabel} · ${companyScope.basis || 'basis unreported'} · ${companyScope.currency || 'currency unreported'}`
          : 'Imported company report period and currency required'
      }
      action={
        hasPermission(permissions, PERMISSIONS.FINANCE_BUDGETS_READ) ? (
          <Open to="/finance">Finance</Open>
        ) : null
      }
    >
      <dl className="space-y-3">
        {[
          ['Revenue', company?.revenue],
          ['Gross profit', company?.grossProfit],
          ['Net income', company?.netIncome],
        ].map(([name, value]) => (
          <div
            key={name}
            className="border-border flex justify-between gap-3 border-b pb-2 text-xs"
          >
            <dt className="text-muted-foreground">{name}</dt>
            <dd className="font-semibold tabular-nums">{recorded(value, companyScope.currency)}</dd>
          </div>
        ))}
      </dl>
      <p className="text-2xs text-muted-foreground mt-3">
        {company?.lastSyncedAt
          ? `Last completed import ${new Date(company.lastSyncedAt).toLocaleString()}.`
          : 'No completed company import is available.'}{' '}
        Comparison and annual plan are unavailable from this report.
      </p>
      <Button
        size="sm"
        variant="outline"
        className="mt-3"
        onClick={() => inspectCompany(read, inspect)}
      >
        Inspect report basis
      </Button>
    </Panel>
  );
  const projectPanel = (
    <Panel
      title="Portfolio exceptions"
      subtitle={`${projects.filter(isProjectException).length} selected records · ranked by recorded risk then model health; missing scores are not low health`}
      action={canReadProjects ? <Open to="/projects">Portfolio</Open> : null}
    >
      {!canReadProjects ? (
        <Gap>Project records are restricted.</Gap>
      ) : (
        <ProjectRows
          rows={[...projects]
            .filter(isProjectException)
            .sort(
              (a, b) =>
                riskOrder.indexOf(a.riskLevel) - riskOrder.indexOf(b.riskLevel) ||
                compareHealth(a, b),
            )}
          onOpen={openProject}
        />
      )}
    </Panel>
  );
  const growthPanel = (
    <Panel
      title="Growth pipeline"
      subtitle="Separate recorded lead and estimate populations; no inferred conversion or weighted contract value."
      action={
        hasPermission(permissions, PERMISSIONS.ESTIMATING_LEADS_READ) ? (
          <Open to="/estimating/bid-intake">Bid intake</Open>
        ) : null
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Small label="Recorded leads" value={number(growth?.leads?.total)} />
        <Small label="Recorded estimates" value={number(growth?.estimates?.total)} />
        <Small
          label="Lead bid dates within 30 days"
          value={number(growth?.leads?.dueWithin30Days)}
        />
        <Small label="Lead bid dates missing" value={number(growth?.leads?.undated)} />
      </div>
      <p className="text-2xs text-muted-foreground mt-3">
        {owner?.growth
          ? `${owner.growth.wonCount} won / ${owner.growth.wonCount + owner.growth.lostCount} decided recorded outcomes${owner.growth.wonCount + owner.growth.lostCount > 0 ? ` · ${number(owner.growth.hitRatePercent)}% hit rate` : ' · hit rate unavailable'}. Outcome cohort differs from current pipeline.`
          : 'Recorded bid outcome source unavailable or restricted.'}
      </p>
    </Panel>
  );
  const constraints = (
    <Panel
      title="Operating constraints"
      subtitle="Native operational records and evidence gaps. Counts may describe overlapping work."
      action={
        hasPermission(permissions, PERMISSIONS.FIELD_DAILY_REPORTS_READ) ? (
          <Open to="/field/daily-reports">Field reports</Open>
        ) : null
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Small label="Blocked field tasks" value={number(ops?.blocked?.blockedTasks)} />
        <Small
          label="Open safety observations"
          value={number(ops?.blocked?.openSafetyObservations)}
        />
        <Small
          label="Missing site reports"
          value={number(ops?.fieldToday?.projectsWithoutDailyReport)}
        />
        <Small label="Overdue quote requests" value={number(ops?.chasing?.overdueQuoteRequests)} />
      </div>
      <p className="text-2xs text-muted-foreground mt-3">
        No verified labor capacity, equipment utilization or safety-rate denominator is supplied.
        Missing or expired certificate evidence does not establish insurance coverage.
      </p>
      <Button
        variant="outline"
        size="sm"
        className="mt-3"
        onClick={() => inspectOperations(read, inspect)}
      >
        Inspect operating exceptions
      </Button>
    </Panel>
  );
  const confidence = (
    <Panel
      title="Data confidence and coverage"
      subtitle="Assembly time is distinct from source observation, import and report dates."
    >
      <div className="grid gap-2 md:grid-cols-2">
        {read ? (
          [read.finance, read.owner, read.operations, read.growth, read.invoices]
            .filter((source) => source !== undefined)
            .map((source) => (
              <div className="border-border rounded border p-2" key={source.label}>
                <div className="flex justify-between gap-2">
                  <p className="text-xs font-medium">{source.label}</p>
                  <Badge variant={source.state === 'available' ? 'neutral' : 'warning'}>
                    {label(source.state)}
                  </Badge>
                </div>
                <p className="text-2xs text-muted-foreground mt-1">{source.scope}</p>
                <p className="text-2xs text-muted-foreground mt-1">{source.note}</p>
              </div>
            ))
        ) : (
          <Gap>
            Executive source composition was not returned. Refresh to read the current contract.
          </Gap>
        )}
      </div>
    </Panel>
  );
  const intelligence = (
    <Panel
      title="Advisor evidence ledger"
      subtitle={`${payload.advisor.recommendations.length} preview rows of ${number(payload.coverage?.recommendationCount)} permitted records; proposal, approval and execution are separate.`}
      action={
        hasPermission(permissions, PERMISSIONS.AI_AGENTS_READ) ? (
          <Open to="/ai-agents">Advisor workspace</Open>
        ) : null
      }
    >
      {payload.advisor.recommendations.length ? (
        <ol className="space-y-2">
          {payload.advisor.recommendations.map((row) => (
            <li key={row.id} className="border-border rounded border p-3">
              <div className="flex flex-wrap justify-between gap-2">
                <h3 className="text-xs font-semibold">{row.title}</h3>
                <Badge variant="ai">{label(row.status)}</Badge>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">{row.recommendedAction}</p>
              <p className="text-2xs text-muted-foreground mt-2">
                {row.agent.name} · recorded {date(row.createdAt)}. Verified business outcome is not
                supplied by this record.
              </p>
              <Button
                className="mt-2"
                size="sm"
                variant="outline"
                onClick={() =>
                  inspect({
                    id: row.id,
                    label: row.title,
                    measure: 'Recorded recommendation',
                    basis: `Status ${row.status}. Estimated impact is not verified savings. Confidence calibration, execution receipt and outcome evidence are unavailable from this contract.`,
                    records: [
                      {
                        id: row.id,
                        title: row.summary,
                        subtitle: JSON.stringify(row.evidence),
                        date: row.createdAt,
                      },
                    ],
                    viewAllHref: hasPermission(permissions, PERMISSIONS.AI_RECOMMENDATIONS_READ)
                      ? '/ai-agents'
                      : undefined,
                    viewAllLabel: 'Review source recommendation',
                  })
                }
              >
                Review evidence
              </Button>
            </li>
          ))}
        </ol>
      ) : (
        <Gap>
          {payload.coverage?.canReadRecommendations === false
            ? 'Advisor recommendations are restricted.'
            : 'The recommendation preview is empty. Source totals and scan coverage must be verified in the advisor workspace.'}
        </Gap>
      )}
    </Panel>
  );
  const rail = (
    <aside
      className="space-y-3 xl:sticky xl:top-0 xl:self-start"
      aria-label="CEO decisions and priorities"
    >
      <Panel
        title="CEO decision queue"
        subtitle="Source workflow review retains its own approval gate."
      >
        {decisions?.items.length ? (
          <ol className="space-y-2">
            {decisions.items.slice(0, 4).map((row) => (
              <li key={row.id} className="border-border border-t pt-2">
                <p className="text-xs font-semibold">{row.title}</p>
                <p className="text-2xs text-muted-foreground mt-1">{row.detail}</p>
                <p className="text-2xs text-muted-foreground mt-1">
                  Waiting since {date(row.waitingSince)} · {number(row.ageHours)} hours. Owner and
                  due date are not stored in this projection.
                </p>
                <Open to={row.href}>Review owning record</Open>
              </li>
            ))}
          </ol>
        ) : (
          <Gap>
            {decisions
              ? decisions.total > 0
                ? 'Waiting decisions were counted, but their preview is unavailable. Refresh or open the owning source register.'
                : 'No waiting decisions in the permitted source cohort.'
              : 'Native decision sources are unavailable or restricted.'}
          </Gap>
        )}
        <p className="text-2xs text-muted-foreground mt-3">
          A dashboard review does not approve, send, pay or execute a source action.
        </p>
      </Panel>
      {priorities}
    </aside>
  );

  if (view === 'system')
    return (
      <div className="grid gap-3 xl:grid-cols-3">
        <Panel
          title="Live event stream"
          subtitle="Runtime connection and tenant-scoped events are distinct from source-data freshness."
        >
          <Badge variant="neutral">Socket {realtime.socketStatus}</Badge>
          <RealtimeEventFeed
            events={realtime.eventsQuery.data}
            isLoading={realtime.eventsQuery.isLoading}
            isError={realtime.eventsQuery.isError}
            error={realtime.eventsQuery.error}
            socketStatus={realtime.socketStatus}
            onRetry={() => void realtime.eventsQuery.refetch()}
          />
        </Panel>
        <EngineeringBibleStatusCard />
        <SystemStatusArea realtimeStatus={realtime.socketStatus} />
      </div>
    );
  if (view === 'finance')
    return (
      <div className="space-y-3">
        {metrics}
        <div className="grid gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
          <FinanceCompanyProfitBridge data={company} canRead={canReadAccounting} />
          {companyPanel}
          {forecastsPanel}
          <Panel
            title="Reviewed forecasts by currency"
            subtitle="Current human-approved WIP cohorts. Currencies remain separate; no FX total or legacy portfolio money sum."
          >
            {finance?.forecast?.cohorts.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="py-2">Currency</th>
                      <th>Projects</th>
                      <th className="text-right">Contract</th>
                      <th className="text-right">Projected margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finance.forecast.cohorts.map((cohort) => (
                      <tr className="border-border border-t" key={cohort.currencyCode}>
                        <td className="py-2">
                          <button
                            className="text-context-primary underline"
                            onClick={() =>
                              inspect({
                                id: `currency:${cohort.currencyCode}`,
                                label: `Reviewed forecast · ${cohort.currencyCode}`,
                                measure: 'Current human-approved WIP cohort',
                                value: recorded(cohort.projectedMargin, cohort.currencyCode),
                                basis: `${cohort.projectCount} reviewed project records; contract ${recorded(cohort.contractValue, cohort.currencyCode)}. No FX conversion.`,
                                records: finance.profitability
                                  .filter(
                                    (row) =>
                                      row.forecastCoverage === 'current' &&
                                      row.snapshot?.currencyCode === cohort.currencyCode,
                                  )
                                  .map((row) => ({
                                    id: row.projectId,
                                    title: `${row.projectNumber} · ${row.projectName}`,
                                    subtitle: `${row.snapshot?.snapshotNumber} · reviewed ${date(row.snapshot?.reviewedAt)}`,
                                    href: '/finance?view=profitability',
                                  })),
                                viewAllHref: '/finance?view=profitability',
                              })
                            }
                          >
                            {cohort.currencyCode}
                          </button>
                        </td>
                        <td>{cohort.projectCount}</td>
                        <td className="text-right tabular-nums">
                          {recorded(cohort.contractValue)}
                        </td>
                        <td className="text-right tabular-nums">
                          {recorded(cohort.projectedMargin)} · {cohort.marginPercent}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Gap>No current reviewed currency cohort is available.</Gap>
            )}
            <p className="text-muted-foreground mt-2 text-xs">
              Unreviewed project records: {number(finance?.forecast?.unreviewedProjectCount)} ·
              unknown currency: {number(finance?.forecast?.unknownCurrencyProjectCount)}.
            </p>
          </Panel>
          <ExecutiveInvoices read={read} inspect={inspect} />
          <Panel
            title="Liquidity evidence"
            subtitle="Cash, receivables and retainage are distinct balances, with no inferred settlement."
          >
            <Small
              label="Recorded cash"
              value={recorded(finance?.summary.netCashPosition, cashCurrency)}
            />
            <p className="text-muted-foreground mt-2 text-xs">
              Source: {finance?.cashSource?.source ?? 'unavailable'} ·{' '}
              {finance?.cashSource?.asOf
                ? `Report as of ${date(finance.cashSource.asOf)}`
                : 'Report as-of date unavailable'}{' '}
              · import observed {finance?.cashSource?.observedAt ?? 'unavailable'} · other currency
              groups excluded: {number(finance?.cashSource?.otherCurrenciesExcluded)}.
            </p>
            <p className="text-muted-foreground mt-2 text-xs">
              Reviewed cash scenarios require an explicit company, currency and period selection
              below. Scenario confidence and remaining-revenue backlog remain unavailable from the
              current source contracts.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => inspectCash(read, inspect)}
            >
              Inspect cash evidence
            </Button>
          </Panel>
        </div>
        <FinanceCashOutlookPanel />
        {confidence}
      </div>
    );
  if (view === 'delivery')
    return (
      <div className="space-y-3">
        <div className="grid gap-3 xl:grid-cols-2">
          {health}
          {constraints}
          {projectPanel}
          <Panel
            title="Recorded completion dates"
            subtitle="Upcoming or past project dates are not inferred critical-path slippage."
          >
            <ul className="space-y-2">
              {projects
                .filter((row) => row.substantialCompletion)
                .sort((a, b) => a.substantialCompletion!.localeCompare(b.substantialCompletion!))
                .slice(0, 8)
                .map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="border-border focus-visible:ring-ring flex w-full justify-between gap-3 rounded border p-2 text-left text-xs focus-visible:ring-2"
                      onClick={() => openProject(row)}
                    >
                      <span>
                        {row.projectNumber} · {row.name}
                      </span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        {date(row.substantialCompletion)}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
            {!projects.some((row) => row.substantialCompletion) ? (
              <Gap>No completion dates are recorded in this portfolio.</Gap>
            ) : null}
          </Panel>
        </div>
        {confidence}
      </div>
    );
  if (view === 'growth')
    return (
      <div className="space-y-3">
        {growthPanel}
        <div className="grid gap-3 xl:grid-cols-2">
          {(['leads', 'estimates'] as const).map((kind) => {
            const source = growth?.[kind];
            const href = kind === 'leads' ? '/estimating/bid-intake' : '/estimating/estimates';
            return (
              <BreakdownBar
                key={kind}
                title={`Recorded ${kind} by stage`}
                basis={`All non-deleted ${kind} records allowed by the native grant; last source edit ${date(source?.latestRecordAt)}. Stages are a snapshot, not sequential conversion.`}
                data={
                  source?.byStatus.map((row) => ({
                    id: row.status,
                    label: label(row.status),
                    values: { records: row.count },
                  })) ?? []
                }
                series={[{ key: 'records', label: 'Source records', token: 'context-primary' }]}
                format="count"
                height={230}
                state={!source ? 'unavailable' : source.total ? 'ok' : 'empty'}
                unavailableMessage="The native source is restricted or unavailable."
                emptyMessage={`No ${kind} are recorded in this source.`}
                onDrill={(id) =>
                  inspect({
                    id,
                    label: `${label(id)} ${kind}`,
                    value: number(source?.byStatus.find((row) => row.status === id)?.count),
                    measure: 'Source records',
                    basis: growth?.note ?? 'Source population unavailable',
                    records: [],
                    recordsAvailable: false,
                    recordsUnavailableReason:
                      'This aggregate does not include individual source rows. Open the owning workspace to inspect and filter its register.',
                    viewAllHref: href,
                    viewAllLabel: `Open ${kind === 'leads' ? 'bid intake' : 'estimates'}`,
                  })
                }
              />
            );
          })}
        </div>
        {confidence}
      </div>
    );
  if (view === 'risk')
    return (
      <div className="space-y-3">
        <div className="grid gap-3 xl:grid-cols-2">
          <BreakdownBar
            title="Recorded risk distribution"
            basis={`${projects.length} selected projects · stored project risk level, not modeled loss probability.`}
            data={payload.projectPerformance.byRisk.map((row) => ({
              id: row.riskLevel,
              label: label(row.riskLevel),
              values: { projects: row.count },
            }))}
            series={[{ key: 'projects', label: 'Project records', token: 'warning' }]}
            format="count"
            height={200}
            state={!canReadProjects ? 'unavailable' : projects.length ? 'ok' : 'empty'}
            onDrill={(id) =>
              inspect({
                id,
                label: `${label(id)} recorded risk`,
                measure: 'Project records',
                value: String(projects.filter((row) => row.riskLevel === id).length),
                basis: payload.coverage?.projectCohort ?? 'Selected portfolio',
                records: projects.filter((row) => row.riskLevel === id).map(projectRecord),
                viewAllHref: '/projects',
              })
            }
          />
          {projectPanel}
          <Panel
            title="Source exposure register"
            subtitle="Native source findings can overlap and are not added into one financial risk score."
          >
            {owner?.exposure ? (
              <ul className="space-y-3">
                {owner.exposure.items.map((row) => (
                  <li key={row.id} className="border-border border-b pb-2">
                    <div className="flex justify-between text-xs">
                      <span>{row.label}</span>
                      <strong>{row.count}</strong>
                    </div>
                    <p className="text-2xs text-muted-foreground mt-1">{row.detail}</p>
                    <Open to={row.href}>Review source records</Open>
                  </li>
                ))}
                {owner.exposure.omitted.map((row) => (
                  <li key={row.id} className="text-2xs text-muted-foreground">
                    {row.label}: restricted · {row.requiredPermission}
                  </li>
                ))}
              </ul>
            ) : (
              <Gap>Exposure sources are unavailable or restricted.</Gap>
            )}
          </Panel>
          <Panel
            title="Active alert preview"
            subtitle={`${payload.alerts.length} shown of ${number(fullActiveAlerts)} open or acknowledged records; no-alert status does not establish safety.`}
          >
            {payload.alerts.length ? (
              <ul className="space-y-2">
                {payload.alerts.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="border-border focus-visible:ring-ring w-full rounded border p-2 text-left text-xs focus-visible:ring-2"
                      onClick={() =>
                        inspect({
                          id: row.id,
                          label: row.title,
                          measure: 'Recorded alert',
                          basis: `${row.category} · ${row.source} · ${row.status}`,
                          records: [{ id: row.id, title: row.message, date: row.createdAt }],
                        })
                      }
                    >
                      {row.title}
                      <span className="text-2xs text-muted-foreground mt-1 block">
                        {label(row.severity)} · {date(row.createdAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <Gap>
                The active alert preview is empty. Refresh or inspect the owning source when its
                complete count is nonzero.
              </Gap>
            )}
          </Panel>
        </div>
        {confidence}
      </div>
    );
  if (view === 'intelligence')
    return (
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-3">
          {intelligence}
          {confidence}
        </div>
        {rail}
      </div>
    );
  return (
    <ExecutiveOverview
      payload={payload}
      permissions={permissions}
      priorities={priorities}
      navigation={overviewNavigation}
      onNavigationChange={onOverviewNavigationChange}
      inspect={inspect}
      healthScore={healthScore}
      healthCoverage={healthCoverage}
      healthSourceAvailable={healthSourceAvailable}
      onInspectCompany={() => inspectCompany(read, inspect)}
      onInspectCash={() => inspectCash(read, inspect)}
      onInspectForecast={(state) => inspectForecast(read, inspect, state)}
      onInspectDecisions={() => inspectDecisions(read, inspect)}
      onInspectField={() => inspectField(read, inspect)}
      onInspectOperations={() => inspectOperations(read, inspect)}
      onInspectProject={openProject}
      onInspectHealth={(id) => {
        if (!canReadProjects || !healthSourceAvailable) return;
        const [low, high] = id === '<60' ? [0, 59] : id.split('-').map(Number);
        const rows = projects.filter((row) => {
          const score = healthScore(row);
          return id === 'UNAVAILABLE'
            ? score === null
            : score !== null && score >= low! && score <= high!;
        });
        inspect({
          id,
          label: id === 'UNAVAILABLE' ? 'No recorded model' : `Recorded health ${id}`,
          measure: 'Project records',
          value: String(rows.length),
          basis: `${rows.length} of ${projects.length} selected projects. ${healthCoverage}. Default scores are excluded from model bands; model health is not a measured safety or schedule score.`,
          records: rows.map(projectRecord),
          viewAllHref: '/projects',
        });
      }}
    />
  );
}

function ExecutiveInvoices({
  read,
  inspect,
}: {
  read: ExecutiveReadModel | undefined;
  inspect: Inspect;
}) {
  const source = read?.invoices;
  const ar = source?.data?.ar,
    ap = source?.data?.ap;
  const href = (direction: 'ar' | 'ap', bucket?: string) =>
    `/finance?view=detail&invoiceDirection=${direction}&agingAsOf=${ar?.asOfDate ?? ap?.asOfDate ?? ''}${bucket ? `&agingBucket=${encodeURIComponent(bucket)}` : ''}`;
  return (
    <div className="min-w-0 space-y-3">
      <BreakdownBar
        title="Receivables by days past due"
        basis={`Current positive open balances · recorded UTC due dates at ${ar?.asOfDate ?? 'as-of unavailable'} · company-wide. Original provider date fidelity and currency are unverified.`}
        data={
          ar?.aging.map((row) => ({
            id: row.bucket,
            label: row.bucket,
            values: { invoices: row.count },
          })) ?? []
        }
        series={[{ key: 'invoices', label: 'Open invoice records', token: 'context-primary' }]}
        format="count"
        height={180}
        state={!ar ? 'unavailable' : ar.summary.count ? 'ok' : 'empty'}
        emptyMessage="No positive open receivable balances are recorded in this source."
        unavailableMessage={source?.note ?? 'Receivables source unavailable or restricted.'}
        onDrill={(bucket) =>
          inspect({
            id: bucket,
            label: `Receivables · ${bucket} days`,
            measure: 'Open invoice records',
            value: number(ar?.aging.find((row) => row.bucket === bucket)?.count),
            basis: source?.scope ?? 'Invoice source unavailable.',
            records: [],
            recordsAvailable: false,
            recordsUnavailableReason:
              'This executive snapshot carries complete aging totals and a single preview row. Open the native register for the selected bucket and invoice evidence.',
            viewAllHref: href('ar', bucket),
            viewAllLabel: 'Open selected receivable records',
          })
        }
      />
      <div className="border-border bg-card grid gap-2 rounded-lg border p-3 sm:grid-cols-2">
        <Small label="Recorded overdue receivables" value={number(ar?.summary.overdueCount)} />
        <Small label="Recorded overdue payables" value={number(ap?.summary.overdueCount)} />
        {ap ? (
          <Open to={href('ap')}>Inspect payable records</Open>
        ) : (
          <p className="text-2xs text-muted-foreground">Payables unavailable or restricted.</p>
        )}
        <p className="text-2xs text-muted-foreground">
          Invoice records are distinct from cash, payment approval and verified bank settlement.
        </p>
      </div>
    </div>
  );
}

function Panel({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-border bg-card min-w-0 rounded-lg border">
      <header className="border-border flex items-start justify-between gap-2 border-b px-3 py-2.5">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="text-2xs text-muted-foreground mt-1">{subtitle}</p>
        </div>
        {action}
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}
function Small({ label: name, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-2xs text-muted-foreground">{name}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
function Gap({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground flex gap-2 py-3 text-xs">
      <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
      {children}
    </p>
  );
}
function Open({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      className="text-2xs text-context-primary focus-visible:ring-ring mt-2 inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline focus-visible:ring-2"
      to={to}
    >
      {children}
      <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
    </Link>
  );
}
function ProjectRows({
  rows,
  onOpen,
}: {
  rows: CommandCenterProjectMarker[];
  onOpen: (row: CommandCenterProjectMarker) => void;
}) {
  return rows.length ? (
    <>
      <ul className="space-y-2">
        {rows.slice(0, 6).map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className="border-border focus-visible:ring-ring flex w-full items-center justify-between gap-3 rounded border p-2 text-left focus-visible:ring-2"
              onClick={() => onOpen(row)}
            >
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium">{row.name}</span>
                <span className="text-2xs text-muted-foreground">
                  {row.projectNumber} · {healthLabel(row)}
                </span>
              </span>
              <RiskBadge level={row.riskLevel.toLowerCase() as 'low'} />
            </button>
          </li>
        ))}
      </ul>
      <p className="text-2xs text-muted-foreground mt-2">
        Showing {Math.min(rows.length, 6)} of {rows.length} exception records.
      </p>
    </>
  ) : (
    <Gap>
      No selected records meet the stated exception rules. This does not establish that all risks
      are measured.
    </Gap>
  );
}
function inspectCompany(read: ExecutiveReadModel | undefined, inspect: Inspect) {
  const source = read?.finance.data?.accounting;
  const returnedScope = companyMetricScope(source, 'revenue');
  inspect({
    id: 'company-report',
    label: 'Imported company report evidence',
    measure: 'Company statement totals',
    basis: source
      ? `${source.source} · ${returnedScope.basis || 'Basis unreported'} · ${returnedScope.dateLabel} · ${returnedScope.currency || 'Currency unreported'}. Completed import ${source.lastSyncedAt ?? 'unavailable'}. Project financial totals are a separate cohort.`
      : 'Imported company financial statement unavailable or restricted.',
    records: source
      ? [
          {
            id: 'statement',
            title: returnedScope.dateLabel,
            subtitle: `Revenue ${recorded(source.revenue, returnedScope.currency)} · gross profit ${recorded(source.grossProfit, returnedScope.currency)} · net income ${recorded(source.netIncome, returnedScope.currency)}`,
          },
        ]
      : [],
    viewAllHref: source ? '/finance' : undefined,
  });
}
function inspectCash(read: ExecutiveReadModel | undefined, inspect: Inspect) {
  const finance = read?.finance.data;
  const source = finance?.cashSource;
  inspect({
    id: 'cash',
    label: 'Recorded cash source',
    measure: 'Cash balance',
    value: recorded(finance?.summary.netCashPosition, source?.currency),
    basis: `Source ${source?.source ?? 'unavailable'} · report as of ${source?.asOf ?? 'unreported'} · import observed ${source?.observedAt ?? 'unreported'}. No FX aggregation; ${source?.otherCurrenciesExcluded ?? 'unknown'} other currency groups excluded. Invoices and retainage are not added to cash.`,
    records: [],
    recordsAvailable: false,
    recordsUnavailableReason:
      'This aggregate does not carry bank-account evidence rows. Review the Finance source workspace with its native grant.',
    viewAllHref: finance ? '/finance' : undefined,
  });
}
function inspectForecast(read: ExecutiveReadModel | undefined, inspect: Inspect, state?: string) {
  const finance = read?.finance.data;
  const rows =
    finance?.profitability.filter(
      (row) => !state || (row.forecastCoverage ?? 'missing') === state,
    ) ?? [];
  inspect({
    id: state ?? 'forecasts',
    label: state ? `${label(state)} forecast evidence` : 'Project forecast evidence',
    measure: 'Qualifying reviewed WIP coverage',
    value: String(rows.length),
    basis:
      'Company-wide eligible active/planning financial projects. Current means consistent human-approved WIP within 30 days, matching positive contract and declared currency. Legacy unreviewed or unknown-currency records are excluded. Currency cohorts remain separate; no FX total is inferred.',
    recordsAvailable: Boolean(finance),
    recordsUnavailableReason: read?.finance.note ?? 'Finance source unavailable.',
    records: rows.map((row) => ({
      id: row.projectId,
      title: `${row.projectNumber} · ${row.projectName}`,
      subtitle: `${label(row.forecastCoverage ?? 'missing')} · ${row.snapshot?.snapshotNumber ?? 'No snapshot'} · projected margin ${recorded(row.margin, row.snapshot?.currencyCode)} · reviewed ${date(row.snapshot?.reviewedAt)}`,
      date: row.snapshot?.asOfDate,
      href: '/finance?view=profitability',
    })),
    viewAllHref: finance ? '/finance?view=profitability' : undefined,
  });
}
function inspectDecisions(read: ExecutiveReadModel | undefined, inspect: Inspect) {
  const source = read?.owner.data?.decisions;
  inspect({
    id: 'decisions',
    label: 'Waiting source decisions',
    measure: 'Native decision records',
    value: number(source?.total),
    basis: `Company-wide permitted sources. ${source?.items.length ?? 0} preview records; totals retain omitted-source caveats. Opening a record does not approve or execute it.`,
    records:
      source?.items.map((row) => ({
        id: row.id,
        title: row.title,
        subtitle: row.detail,
        date: row.waitingSince,
        href: row.href,
      })) ?? [],
    recordsAvailable: Boolean(source),
    recordsUnavailableReason: 'Decision sources are unavailable or restricted.',
  });
}
function inspectField(read: ExecutiveReadModel | undefined, inspect: Inspect) {
  const source = read?.operations.data?.fieldToday;
  inspect({
    id: 'field',
    label: 'Current field reporting evidence',
    measure: 'Reported on-site headcount',
    value: number(source?.totalHeadcount),
    basis: `${source?.date ?? 'Date unavailable'} · ${source?.timeZone ?? 'Calendar unavailable'}. Reported crew/headcount is not labor utilization or available capacity.`,
    records:
      source?.missingReports.map((row) => ({
        id: row.projectId,
        title: `${row.projectNumber} · ${row.projectName}`,
        subtitle: `${row.headcount} reported headcount · daily report missing`,
        href: row.href,
      })) ?? [],
    recordsAvailable: Boolean(source),
    recordsUnavailableReason: 'Field report source unavailable or restricted.',
    viewAllHref: source ? '/field/daily-reports' : undefined,
  });
}
function inspectOperations(read: ExecutiveReadModel | undefined, inspect: Inspect) {
  const source = read?.operations.data;
  inspect({
    id: 'operations',
    label: 'Operating exceptions',
    measure: 'Native field and procurement records',
    basis:
      'Bounded preview rows retain the source totals. Overlapping exception types are not added into one risk or performance rate.',
    records: [
      ...(source?.blocked?.items ?? []).map((row) => ({
        id: row.id,
        title: row.title,
        subtitle: row.reason,
        date: row.since,
        href: row.href,
      })),
      ...(source?.chasing?.items ?? []).map((row) => ({
        id: row.id,
        title: row.title,
        subtitle: row.detail,
        date: row.since,
        href: row.href,
      })),
    ],
    recordsAvailable: Boolean(source),
    recordsUnavailableReason: 'Operating sources unavailable or restricted.',
  });
}
